// =====================================================
// COMPOSANT: FluxSection — section « Flux d'entrées » (RUN 2)
// =====================================================
// Deux cartes (Entrées de la semaine / du mois) + histogramme « ID entrées »
// (barres N vs N-1) avec bascule hebdo/quotidien. L'histogramme est une donnée
// ID → enveloppé dans <MaskableId> (masqué par défaut). Recharts via le
// ChartContainer maison (pas de barres CSS). Design system crm-4l (zinc).

import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Legend } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import type { FluxCardData, HistoBucket, HistoGranularity } from '@/lib/dashboard-stats';
import { FluxCard } from './FluxCard';

// N = édition courante (zinc-900), N-1 = édition précédente (zinc-300).
const COLOR_N = '#18181b';
const COLOR_N1 = '#d4d4d8';

interface GranularityToggleProps {
  value: HistoGranularity;
  onChange: (g: HistoGranularity) => void;
}

/** Bascule hebdo/quotidien (segmented control zinc, idiome crm-4l). */
function GranularityToggle({ value, onChange }: GranularityToggleProps) {
  const opts: { value: HistoGranularity; label: string }[] = [
    { value: 'week', label: 'Hebdo' },
    { value: 'day', label: 'Quotidien' },
  ];
  return (
    <div role="tablist" aria-label="Granularité" className="inline-flex rounded-lg bg-zinc-100 p-1">
      {opts.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={[
              'rounded-md px-3 py-1 text-sm font-medium transition-colors',
              active ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700',
            ].join(' ')}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

interface IdHistogramProps {
  buckets: HistoBucket[];
  showN1: boolean;
  granularity: HistoGranularity;
  hasN1: boolean;
}

function IdHistogram({ buckets, showN1, granularity, hasN1 }: IdHistogramProps) {
  const renderN1 = showN1 && hasN1;
  const config: ChartConfig = {
    n: { label: 'ID (N)', color: COLOR_N },
    n1: { label: 'ID (N-1)', color: COLOR_N1 },
  };
  const empty = buckets.every((b) => b.n === 0 && (b.n1 ?? 0) === 0);

  if (empty) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-zinc-200 text-xs text-zinc-400">
        Aucune inscription définitive sur la période.
      </div>
    );
  }

  return (
    <ChartContainer config={config} className="aspect-auto h-64 w-full">
      <BarChart data={buckets} margin={{ top: 10, right: 12, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11, fill: '#71717a' }}
          interval={granularity === 'day' ? 1 : 0}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
          width={28}
          tick={{ fontSize: 11, fill: '#71717a' }}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        {renderN1 ? <Legend content={<HistoLegend />} /> : null}
        <Bar dataKey="n" name="ID (N)" fill={COLOR_N} radius={[4, 4, 0, 0]} maxBarSize={40} />
        {renderN1 ? (
          <Bar dataKey="n1" name="ID (N-1)" fill={COLOR_N1} radius={[4, 4, 0, 0]} maxBarSize={40} />
        ) : null}
      </BarChart>
    </ChartContainer>
  );
}

function HistoLegend() {
  return (
    <div className="flex items-center justify-center gap-4 pt-3 text-xs text-zinc-600">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: COLOR_N }} />
        ID (N)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: COLOR_N1 }} />
        ID (N-1)
      </span>
    </div>
  );
}

interface FluxSectionProps {
  flux: { week: FluxCardData; month: FluxCardData };
  histogram: { week: HistoBucket[]; day: HistoBucket[] };
  showN1: boolean;
  hasN1: boolean;
}

export function FluxSection({ flux, histogram, showN1, hasN1 }: FluxSectionProps) {
  const [granularity, setGranularity] = useState<HistoGranularity>('week');
  const buckets = histogram[granularity];

  return (
    <section className="flex flex-col gap-6">
      {/* Titre de section */}
      <div className="flex items-baseline gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-700">
          Flux d'entrées
        </h2>
        <div className="h-px flex-1 bg-zinc-200" />
      </div>

      {/* Cartes semaine / mois */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <FluxCard
          title="Entrées de la semaine"
          subtitle="7 derniers jours"
          card={flux.week}
          showN1={showN1}
        />
        <FluxCard
          title="Entrées du mois"
          subtitle="30 derniers jours"
          card={flux.month}
          showN1={showN1}
        />
      </div>

      {/* Histogramme ID entrées — donnée ID → masquable */}
      <Card className="border border-zinc-200">
        <CardContent className="flex flex-col gap-4 px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col">
              <h3 className="text-sm font-semibold text-zinc-950">
                ID entrées par {granularity === 'week' ? 'semaine' : 'jour'}
              </h3>
              <span className="text-xs text-zinc-500">
                Inscriptions définitives entrées · N vs N-1
              </span>
            </div>
            <GranularityToggle value={granularity} onChange={setGranularity} />
          </div>
          <IdHistogram buckets={buckets} showN1={showN1} granularity={granularity} hasN1={hasN1} />
        </CardContent>
      </Card>
    </section>
  );
}
